import React, { useState } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  Pagination,
  FormControl,
  Select,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Skeleton from "@mui/material/Skeleton";
import ErrorPage from "../Components/ErrorPage";
import ProductAdd from "./ProductAdd";
import ProductEdit from "./ProductEdit";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";
import { exportProductsToPDF } from "../../utils/pdfUtils";

import { formatJalaliDateTime } from "../utils/dateFormat";
const ProductIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch products using search and category filters with pagination
  const productsQuery = `shop/products/?search=${searchTerm}${
    selectedCategory ? `&category=${selectedCategory}` : ""
  }&page=${page}&page_size=${itemsPerPage}`;
  const { data: productsData, isLoading, isError } = useFetchObjects(
    ["product", searchTerm, selectedCategory, page, itemsPerPage],
    productsQuery,
    token
  );

  const data = productsData?.results || [];
  const totalCount = productsData?.count || 0;
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  // Fetch available categories for filtering
  const { data: categoriesData } = useFetchObjects(
    ["category"],
    "shop/categories",
    token
  );

  const categories = categoriesData?.results || [];

  const { handleDelete, ConfirmDialog } = useDelete("shop/products", token);



  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (event) => {
    const value = event.target.value;
    setItemsPerPage(value === 'all' ? totalCount : value);
    setPage(1);
  };

  const handleExportToPDF = async () => {
    console.log('Token:', token ? 'Present' : 'Missing');
    
    try {
      const url = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/shop/products/?page_size=999999`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        const allProducts = result.results || result || [];
        console.log('All products fetched:', allProducts.length);
        await exportProductsToPDF(allProducts, searchTerm, selectedCategory, categories, token);
      } else {
        console.error('Auth failed, using current data');
        await exportProductsToPDF(data || [], searchTerm, selectedCategory, categories, token);
      }
    } catch (error) {
      console.error('Error:', error);
      await exportProductsToPDF(data || [], searchTerm, selectedCategory, categories, token);
    }
  };

  if (isError) {
    return <ErrorPage message={t("product.failedToLoadProducts")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("product.productList")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportToPDF}
            disabled={!data || data.length === 0}
          >
            Export PDF
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenAdd(true)}
          >
            {t("product.addNewProduct")}
          </Button>
        </Box>
      </Box>
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("product.dataLoadedOn")} {formatJalaliDateTime()}
      </Typography>
      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          variant="outlined"
          placeholder={t("product.searchProducts")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          variant="outlined"
          placeholder={t("product.filterByCategory")}
          value={selectedCategory}
          onChange={handleCategoryChange}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">{t("product.allCategories")}</MenuItem>
          {categories &&
            categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.name}
              </MenuItem>
            ))}
        </TextField>
        <FormControl variant="outlined" sx={{ minWidth: 120 }}>
          <InputLabel>Items per page</InputLabel>
          <Select
            value={itemsPerPage > 50 ? 'all' : itemsPerPage}
            onChange={handleItemsPerPageChange}
            label="Items per page"
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {/* Product Table */}
      <TableContainer
        component={Paper}
        sx={{ mt: 2, maxHeight: 530, overflowY: "auto" }}
      >
        <Table sx={{ minWidth: 750 }} aria-label="product table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("product.id")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {/* {t("product.brandName")}  */}
              </TableCell>
              <TableCell sx={{ color: "white" }}>{t("product.name")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("product.category")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>{t("product.code")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("product.description")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("product.image")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("product.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {[
                      "ID",
                      "Product Name",
                      "Category",
                      "Code",
                      "Description",
                      "Image",
                      "Action",
                    ].map((_, colIndex) => (
                      <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : data.map((product, index) => (
                  <TableRow key={product.id} hover>
                    <TableCell>{(page - 1) * itemsPerPage + index + 1}</TableCell>
                    <TableCell>{product.brand_name || ''}</TableCell>
                    <TableCell>{product.name}</TableCell>
                    <TableCell>
                      {product.category
                        ? product.category_details.name
                        : t("product.noCategory")}
                    </TableCell>
                    <TableCell>{product.code || "-"}</TableCell>
                    <TableCell>{product.description}</TableCell>
                    <TableCell>
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.name}
                          style={{ width: "50px", height: "auto" }}
                        />
                      ) : (
                        t("product.noImage")
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(product);
                          setOpenEdit(true);
                        }}
                      >
                        {t("product.edit")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(product.id)}
                      >
                        {t("product.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Showing {((page - 1) * itemsPerPage) + 1} to {Math.min(page * itemsPerPage, totalCount)} of {totalCount} products
        </Typography>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          showFirstButton
          showLastButton
        />
      </Box>

      {openAdd && (
        <ProductAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}
      {openEdit && (
        <ProductEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
        />
      )}
      <ConfirmDialog />
    </Box>
  );
};

export default ProductIndex;
